import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Target,
  Lightbulb,
  Calendar,
  Send,
  AlertCircle,
  CheckCircle,
  Share2,
  Clock,
  Sparkles,
  Briefcase,
  Heart,
  Users,
  Zap,
  MessageSquare
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { streamOpenAIResponse } from '../lib/openai';

interface QuizAnswers {
  brandName: string;
  brandDescription: string;
  brandMission: string;
  brandVibe: string;
  platforms: string[];
}

const BrandCampaignQuiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({
    brandName: '',
    brandDescription: '',
    brandMission: '',
    brandVibe: '',
    platforms: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<{
    themes: string[];
    hooks: string[];
    posts: Array<{
      content: string;
      platform: string;
      theme: string;
      scheduledFor?: string;
    }>;
  } | null>(null);

  const steps = [
    {
      title: "Tell us about your brand",
      description: "Enter your brand's basic information",
      fields: [
        {
          id: 'brandName',
          label: 'Brand Name',
          type: 'text',
          placeholder: 'Enter your brand name'
        },
        {
          id: 'brandDescription',
          label: 'What does your brand do?',
          type: 'textarea',
          placeholder: 'Describe your products/services and target market'
        },
        {
          id: 'brandMission',
          label: 'What do you want people to remember?',
          type: 'textarea',
          placeholder: 'Your brand mission or key message'
        }
      ]
    },
    {
      title: "What vibe do you want to give off?",
      description: "Choose your brand's personality",
      options: [
        { id: 'professional', label: 'Professional', icon: Briefcase },
        { id: 'fun', label: 'Fun & Playful', icon: Heart },
        { id: 'community', label: 'Community-Focused', icon: Users },
        { id: 'premium', label: 'Luxury/Premium', icon: Sparkles },
        { id: 'trendy', label: 'Trendy/Relatable', icon: Zap }
      ]
    },
    {
      title: "Where do you want to build presence?",
      description: "Select your target platforms",
      options: [
        { id: 'twitter', label: 'Twitter', icon: 'twitter' },
        { id: 'linkedin', label: 'LinkedIn', icon: 'linkedin' },
        { id: 'instagram', label: 'Instagram', icon: 'instagram' },
        { id: 'facebook', label: 'Facebook', icon: 'facebook' },
        { id: 'youtube', label: 'YouTube', icon: 'youtube' }
      ],
      multiSelect: true
    }
  ];

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      await handleSubmit();
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Save campaign data
      const { data: campaign, error: campaignError } = await supabase
        .from('brand_campaigns')
        .insert({
          user_id: user.id,
          brand_name: answers.brandName,
          brand_description: answers.brandDescription,
          brand_mission: answers.brandMission,
          brand_vibe: answers.brandVibe,
          platforms: answers.platforms
        })
        .select()
        .single();

      if (campaignError) throw campaignError;

      // Generate content using OpenAI
      const prompt = `Create a brand awareness campaign for ${answers.brandName}.

Brand details:
${answers.brandDescription}
Mission: ${answers.brandMission}
Vibe: ${answers.brandVibe}

Platforms: ${answers.platforms.join(', ')}

Format your response as JSON with the following structure:
{
  "themes": ["content theme 1", "content theme 2", "content theme 3"],
  "hooks": ["attention hook 1", "attention hook 2", "attention hook 3"],
  "posts": [
    {
      "content": "post content",
      "platform": "platform name",
      "theme": "content theme",
      "scheduledFor": "suggested posting time"
    }
  ]
}`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            const data = JSON.parse(responseText);
            setGeneratedContent(data);
          } catch (e) {
            // Partial response, continue collecting
          }
        },
        (error) => {
          throw error;
        }
      );

      // Save generated content
      if (generatedContent) {
        const contentPromises = generatedContent.posts.map(post => 
          supabase
            .from('brand_campaign_content')
            .insert({
              campaign_id: campaign.id,
              content: post.content,
              platform: post.platform,
              content_theme: post.theme,
              hook: generatedContent.hooks[0],
              scheduled_for: post.scheduledFor
            })
        );

        await Promise.all(contentPromises);
      }

    } catch (err) {
      console.error('Error submitting quiz:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderCurrentStep = () => {
    const step = steps[currentStep];
    if (!step) return null;

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {step.title}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {step.description}
          </p>
        </div>

        {'fields' in step ? (
          <div className="space-y-4">
            {step.fields.map(field => (
              <div key={field.id}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {field.label}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={answers[field.id as keyof QuizAnswers] as string}
                    onChange={(e) => setAnswers(prev => ({
                      ...prev,
                      [field.id]: e.target.value
                    }))}
                    placeholder={field.placeholder}
                    rows={4}
                    className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                  />
                ) : (
                  <input
                    type={field.type}
                    value={answers[field.id as keyof QuizAnswers] as string}
                    onChange={(e) => setAnswers(prev => ({
                      ...prev,
                      [field.id]: e.target.value
                    }))}
                    placeholder={field.placeholder}
                    className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {step.options.map((option) => {
              const Icon = typeof option.icon === 'string' ? Building2 : option.icon;
              const field = currentStep === 1 ? 'brandVibe' : 'platforms';
              const isSelected = step.multiSelect
                ? answers[field] instanceof Array && answers[field].includes(option.id)
                : answers[field] === option.id;

              return (
                <motion.button
                  key={option.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setAnswers(prev => ({
                    ...prev,
                    [field]: step.multiSelect
                      ? (prev[field] instanceof Array
                          ? isSelected
                            ? prev[field].filter(id => id !== option.id)
                            : [...prev[field], option.id]
                          : [option.id])
                      : option.id
                  }))}
                  className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${
                    isSelected
                      ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                      : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="ml-3 font-medium text-gray-900 dark:text-white">
                    {option.label}
                  </span>
                  {isSelected && (
                    <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  )}
                </motion.button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!generatedContent) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Your Brand Awareness Campaign
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Here's your personalized strategy to build {answers.brandName}'s presence
          </p>
        </div>

        {/* Content Themes */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Lightbulb className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
              Content Themes
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {generatedContent.themes.map((theme, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800/50 rounded-lg p-4"
              >
                <p className="text-gray-700 dark:text-gray-300">{theme}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Engagement Hooks */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <MessageSquare className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-lg font-semibold text-purple-900 dark:text-purple-100">
              Engagement Hooks
            </h3>
          </div>
          <div className="space-y-3">
            {generatedContent.hooks.map((hook, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800/50 rounded-lg p-4"
              >
                <p className="text-gray-700 dark:text-gray-300">{hook}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Generated Posts */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Campaign Posts
          </h3>
          {generatedContent.posts.map((post, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-sm">
                      {post.platform}
                    </span>
                    <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-full text-sm">
                      {post.theme}
                    </span>
                    {post.scheduledFor && (
                      <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.scheduledFor}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-900 dark:text-white">{post.content}</p>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <button className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">
                    <Share2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={() => {
              setGeneratedContent(null);
              setCurrentStep(0);
              setAnswers({
                brandName: '',
                brandDescription: '',
                brandMission: '',
                brandVibe: '',
                platforms: []
              });
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Start Over
          </button>
          <div className="space-x-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate More
            </button>
            <button
              onClick={() => navigate('/calendar')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule All
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Generating your campaign...</p>
        </div>
      ) : generatedContent ? (
        renderResults()
      ) : (
        <div className="space-y-8">
          {/* Progress bar */}
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-400">
                  Progress
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {Math.round((currentStep / (steps.length - 1)) * 100)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200 dark:bg-indigo-900/40">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / (steps.length - 1)) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600"
              />
            </div>
          </div>

          {renderCurrentStep()}

          <div className="flex justify-between pt-6">
            {currentStep > 0 ? (
              <button
                onClick={handleBack}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/campaign-generator')}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
            <button
              onClick={handleNext}
              disabled={
                currentStep === 0 ? !answers.brandName || !answers.brandDescription || !answers.brandMission :
                currentStep === 1 ? !answers.brandVibe :
                answers.platforms.length === 0
              }
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                (currentStep === 0 && answers.brandName && answers.brandDescription && answers.brandMission) ||
                (currentStep === 1 && answers.brandVibe) ||
                (currentStep === 2 && answers.platforms.length > 0)
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {currentStep === steps.length - 1 ? (
                <>
                  Generate Campaign
                  <Send className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandCampaignQuiz;